import { useParams } from 'react-router-dom'
import { FieldArray, Form, Formik } from 'formik'
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import theme from 'lib/theme'
import { contentLanguages } from 'lib/languages'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import { useAlert } from 'providers/AlertProvider'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import AddIcon from '@mui/icons-material/Add'
import { t } from 'i18next'
import { TooltipText } from 'components/TooltipText/TooltipText'
import { InputWithDropdown } from 'components/InputWithDropdown/InputWithDropdown'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { v4 as getRandomId } from 'uuid'
import { formatMultiLanguageField } from 'lib/utils'
import { ProductAttributeGroup } from 'types/model/product'
import { ProductAttribute } from 'types/api/product'
import { useProductInfo } from './Product'
import { convertBooleanToString, formatLangContentField } from './utils'
import { LanguageSelector } from './LanguageSelector'
import { AttributeGroup } from './AttributeGroup'

type FormikFieldType = {
  storageInformation: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  preparationUsage: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  selectedLanguage: string
  attributeGroup: ProductAttributeGroup[]
}

export const AdditionalInfo = () => {
  const { setAlert } = useAlert()
  const { product, isLoading, error, masterProduct } = useProductInfo()
  const { masterProductId } = useParams()
  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialValues = {
    preparationUsage: {
      options: product.preparationUsage,
      contentLanguage: contentLanguages[0],
    },
    storageInformation: {
      options: product.storageInformation,
      contentLanguage: contentLanguages[0],
    },
    selectedLanguage: contentLanguages[0],
    attributeGroup: product.productAttributeGroup,
  }

  const newGroupTemplate = {
    id: getRandomId(),
    productAttributeGroupName: {
      options: formatMultiLanguageField(undefined),
      contentLanguage: contentLanguages[0],
    },
    productAttribute: [{ id: getRandomId() }],
  }

  const formatProductAttribute = (attributeGroups: ProductAttributeGroup[]) => {
    return attributeGroups.map((group) => {
      const productAttribute = group.productAttribute.reduce(
        (acc, attribute) => {
          const productAttributeName = attribute.productAttributeName?.options
            ? formatLangContentField(attribute.productAttributeName?.options)
            : null
          const {
            productAttributeValueBoolean,
            isFacetAttribute,
            productAttributeValueNumeric,
            productAttributeValueString,
            id,
          } = attribute
          const keys = Object.keys(attribute)
          if (keys.length === 1 && keys[0] === 'id') return acc

          // send only one of attribute value boolean, numeric, or string
          const formattedAttribute = {
            isFacetAttribute,
            productAttributeExtId: id,
            productAttributeTypeCode: 'DESCRIPTIVE',
            ...(productAttributeName && { productAttributeName }),
            ...(convertBooleanToString(productAttributeValueBoolean) && {
              productAttributeValueBoolean,
            }),
            ...(productAttributeValueNumeric && {
              productAttributeValueNumeric: Number(
                productAttributeValueNumeric
              ),
            }),
            ...(productAttributeValueString?.options &&
              !Object.values(productAttributeValueString?.options || {}).every(
                (x) => x === ''
              ) && {
                productAttributeValueString: formatLangContentField(
                  productAttributeValueString?.options
                ),
              }),
          }
          acc.push(formattedAttribute)
          return acc
        },
        [] as ProductAttribute[]
      )
      return {
        productAttributeGroupName: formatLangContentField(
          group.productAttributeGroupName?.options
        ),
        productAttributeGroupExtId: getRandomId(),
        productAttribute,
      }
    })
  }

  const handleContentLanguageChange = (
    value: string,
    setFieldValue: (field: string, value: string) => void,
    values: FormikFieldType
  ) => {
    const fieldsToChange = ['preparationUsage', 'storageInformation']
    fieldsToChange.forEach((field) => {
      setFieldValue(`${field}.contentLanguage`, value)
    })
    const numberOfGroups = values.attributeGroup?.length || 1
    for (let i = 0; i < numberOfGroups; i += 1) {
      setFieldValue(
        `attributeGroup.${i}.productAttributeGroupName.contentLanguage`,
        value
      )
    }
    setFieldValue('selectedLanguage', value)
  }

  const handleSubmit = async (values: FormikFieldType) => {
    const masterProductCopy = { ...masterProduct }
    const temp = masterProductCopy.tradeItem.tradeItemInformation.extensions

    const formattedStorageInfo = values.storageInformation
      ? formatLangContentField(values.storageInformation.options)
      : null
    const formattedPreparation = values.preparationUsage
      ? formatLangContentField(values.preparationUsage.options)
      : null
    const productAttributeGroup = formatProductAttribute(values.attributeGroup)
    temp.consumerInstructionsModule = {
      ...temp.consumerInstructionsModule,
      consumerInstructions: {
        ...temp.consumerInstructionsModule?.consumerInstructions,
        ...(formattedStorageInfo && {
          consumerStorageInstructions: formattedStorageInfo,
        }),
      },
    }
    temp.foodAndBeveragePreparationServingModule = {
      ...temp.foodAndBeveragePreparationServingModule,
      preparationServing: [
        {
          ...temp.foodAndBeveragePreparationServingModule
            ?.preparationServing?.[0],
          ...(formattedPreparation && {
            preparationInstructions: formattedPreparation,
          }),
        },
      ],
    }

    temp.dgProductAttributeModule = {
      productAttributeGroup,
    }

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  if (isLoading) return <LoadingIndicator />
  if (error) return <h1>Error</h1>

  return (
    <Box maxWidth='500px'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ values, dirty }) => (
          <Form style={{ height: '100%' }}>
            <Stack mt={5} spacing={3} mb={8}>
              {contentLanguages?.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: theme.spacing(2),
                    right: theme.spacing(2),
                    zIndex: 1000,
                  }}>
                  <LanguageSelector
                    selectedValue={values.selectedLanguage}
                    handleChange={(
                      value: string,
                      setFieldValue: (field: string, value: string) => void
                    ) => {
                      handleContentLanguageChange(value, setFieldValue, values)
                    }}
                  />
                </Box>
              )}
              <Typography variant='body2'>
                {t('preparationAndUsage')}
              </Typography>
              <InputWithDropdown
                multiline
                label={t('preparationAndUsage')}
                value={
                  values.preparationUsage.options[
                    values.preparationUsage.contentLanguage
                  ]
                }
                selectedOption={values.preparationUsage.contentLanguage}
                dropdownFieldName='preparationUsage.contentLanguage'
                inputFieldName='preparationUsage.options'
                inputOptions={values.preparationUsage.options}
              />
              <Typography variant='body2'>{t('storageInformation')}</Typography>
              <InputWithDropdown
                multiline
                label={t('storageInformation')}
                value={
                  values.storageInformation.options[
                    values.storageInformation.contentLanguage
                  ]
                }
                selectedOption={values.storageInformation.contentLanguage}
                dropdownFieldName='storageInformation.contentLanguage'
                inputFieldName='storageInformation.options'
                inputOptions={values.storageInformation.options}
              />
              <Box>
                <Typography variant='body2'>{t('attributes')}</Typography>
                <Divider
                  sx={{
                    color: theme.palette.neutral.grey7,
                    width: '100%',
                    marginRight: theme.spacing(4),
                    marginTop: theme.spacing(1),
                  }}
                />
              </Box>
              <FieldArray name='attributeGroup'>
                {({ push }) => (
                  <>
                    {values.attributeGroup.map((attributeGroup, index) => (
                      <AttributeGroup
                        attributeGroup={attributeGroup}
                        index={index}
                        key={attributeGroup.id}
                      />
                    ))}
                    <Box>
                      <IconButton onClick={() => push(newGroupTemplate)}>
                        <TooltipText text={t('newGroup')}>
                          <AddIcon />
                        </TooltipText>
                      </IconButton>
                    </Box>
                  </>
                )}
              </FieldArray>
            </Stack>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
